/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { featuresSetRemoteSessionAutoRefreshPolicy } from "../funcs/featuresSetRemoteSessionAutoRefreshPolicy.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetRemoteSessionAutoRefreshPolicyRequest,
  SetRemoteSessionAutoRefreshPolicySecurity,
} from "../models/operations/setremotesessionautorefreshpolicy.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetRemoteSessionAutoRefreshPolicyMutationVariables = {
  request: SetRemoteSessionAutoRefreshPolicyRequest;
  security?: SetRemoteSessionAutoRefreshPolicySecurity | undefined;
  options?: RequestOptions;
};

export type SetRemoteSessionAutoRefreshPolicyMutationData = void;

export type SetRemoteSessionAutoRefreshPolicyMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setRemoteSessionAutoRefreshPolicy features
 *
 * @remarks
 * Set the organization policy for automatic remote-session refresh.
 */
export function useSetRemoteSessionAutoRefreshPolicyMutation(
  options?: MutationHookOptions<
    SetRemoteSessionAutoRefreshPolicyMutationData,
    SetRemoteSessionAutoRefreshPolicyMutationError,
    SetRemoteSessionAutoRefreshPolicyMutationVariables
  >,
): UseMutationResult<
  SetRemoteSessionAutoRefreshPolicyMutationData,
  SetRemoteSessionAutoRefreshPolicyMutationError,
  SetRemoteSessionAutoRefreshPolicyMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetRemoteSessionAutoRefreshPolicyMutation(client, options),
    ...options,
  });
}

export function mutationKeySetRemoteSessionAutoRefreshPolicy(): MutationKey {
  return ["@gram/client", "features", "setRemoteSessionAutoRefreshPolicy"];
}

export function buildSetRemoteSessionAutoRefreshPolicyMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetRemoteSessionAutoRefreshPolicyMutationVariables,
  ) => Promise<SetRemoteSessionAutoRefreshPolicyMutationData>;
} {
  return {
    mutationKey: mutationKeySetRemoteSessionAutoRefreshPolicy(),
    mutationFn: function setRemoteSessionAutoRefreshPolicyMutationFn({
      request,
      security,
      options,
    }): Promise<SetRemoteSessionAutoRefreshPolicyMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(featuresSetRemoteSessionAutoRefreshPolicy(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
